import { useQuery, keepPreviousData } from '@tanstack/react-query';
import API_ENDPOINTS from 'services/urls';
import axiosInstance from 'ns_libs/nsAxios';
import { TOTAL_ANTICIPATED_COST } from 'views/ProFormaTable/constants';
import { getLocalStorage } from 'helpers/localStorage/helpers';
import { PageSpecificKeys } from 'helpers/localStorage';
import { IPipelineReportQueryParams, IPipelineReportDealsResponse, ProFormaData } from '../types';

const getPipelineReportDeals = async ({
    pageSize,
    currentPage,
    orgId,
    selectedColumns,
    configuredFilters,
    proFormaData,
}: IPipelineReportQueryParams): Promise<IPipelineReportDealsResponse> => {
    const offset = (currentPage - 1) * pageSize;

    const params: any = {
        page: offset,
        page_size: pageSize,
        selected_columns: selectedColumns,
        configured_filters: JSON.stringify(configuredFilters),
        pro_forma_data: JSON.stringify(proFormaData),
    };

    const response = await axiosInstance.get(`${API_ENDPOINTS.ORG_REPORTS(orgId)}/pipeline_report`, {
        params,
        paramsSerializer: params => {
            const queryString = Object.entries(params)
                .flatMap(([key, value]) => {
                    if (key === 'configured_filters' && typeof value === 'string') {
                        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                    }
                    if (Array.isArray(value)) {
                        return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
                    }
                    if (value !== undefined && value !== null) {
                        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
                    }
                    return [];
                })
                .join('&');
            return queryString;
        },
    });

    return response.data;
};

export const useGetPipelineReportDeals = ({
    pageSize, currentPage, orgId, selectedColumns, configuredFilters,
}: Omit<IPipelineReportQueryParams, 'forecastMetric' | 'scenarioId'>) => {
    // Read forecast_metric and scenario_id from localStorage ProFormaPage object
    const getLocalStorageValues = (): ProFormaData[] => {
        try {
            const proFormaPageData = getLocalStorage(PageSpecificKeys.ProFormaPage);
            if (proFormaPageData) {
                const parsedData: Record<string, any> = proFormaPageData;

                return Object.entries(parsedData).map(([id, config]) => {
                    const result: ProFormaData = {
                        id: parseInt(id, 10),
                    };

                    if (config.selectedScenario !== undefined) {
                        result.selectedScenarioId = config.selectedScenario;
                    }

                    if (config.forecastMetric !== undefined) {
                        result.selectedForecastMetric = config.forecastMetric;
                    } else {
                        result.selectedForecastMetric = TOTAL_ANTICIPATED_COST;
                    }

                    return result;
                });
            }
        } catch (error) {
            console.warn('Error parsing ProFormaPage from localStorage:', error);
        }

        return [];
    };

    const proFormaData = getLocalStorageValues();
    return useQuery({
        queryKey: ['getPipelineReportDeals', orgId, currentPage, pageSize, selectedColumns, configuredFilters, proFormaData],
        queryFn: () => getPipelineReportDeals({
            pageSize, currentPage, orgId, selectedColumns, configuredFilters, proFormaData,
        }),
        staleTime: 5000,
        refetchOnMount: true,
        enabled: selectedColumns.length > 0,
        placeholderData: keepPreviousData,
    });
};
